import { Fragment, type ReactElement, useCallback, useMemo, useState } from 'react';
import uniqBy from 'lodash/uniqBy';
import { type ValueOf } from '@direct-frontend/stdlib';
import { trackGoal } from '@/entities/trackGoal';
import { type MapSubmitButtonComponent } from '@/shared/api/map';
import { type OrderDto } from '@/shared/api/order';
import { getRequestContext } from '@/shared/lib/requestContext';
import { PickupNewAddressDialog, type PickupNewAddressDialogProps } from './components/PickupNewAddressDialog';
import { PickupSavedAddressesDialog } from './components/PickupSavedAddressesDialog';
import type { PickupAddressValue } from './types/PickupAddressValue';

const DIALOGS = {
    SAVED_ADDRESSES: 'saved-address',
    NEW_ADDRESS: 'new-address',
} as const;

interface PickupAddressPickerPayload {
    onOpenModal: VoidFunction;
}

export interface PickupPointPickerProps extends Omit<PickupNewAddressDialogProps, 'isOpen' | 'onClose'> {
    reinitializeOrderForm: (initialOrder: OrderDto, updatedOrder: OrderDto) => void;
    locality: string;
    children: (payload: PickupAddressPickerPayload) => ReactElement;
    submitButton: MapSubmitButtonComponent;
}

export const PickupAddressPicker = (props: PickupPointPickerProps) => {
    const { account } = getRequestContext();

    const [activeDialog, setActiveDialog] = useState<ValueOf<typeof DIALOGS> | null>(null);
    const {
        order,
        storeSlug,
        defaultLocation,
        children,
        onChange,
        value,
        locality,
        submitButton,
        reinitializeOrderForm,
    } = props;

    const predefinedAddresses: Array<PickupAddressValue> = useMemo(
        () =>
            uniqBy(
                account.addresses
                    .filter((address) => address.pickupPointLocality === locality)
                    .map<PickupAddressValue>((address) => {
                        return {
                            locality: address.pickupPointLocality,
                            address: address.pickupPointAddress,
                            pickupPointId: address.pickupPointId,
                            serviceType: address.pickupPointServiceType,
                        };
                    }),
                'pickupPointId'
            ),
        [account, locality]
    );

    const onOpenModal = useCallback(() => {
        trackGoal('pickup_popup_open');

        if (predefinedAddresses.length > 0) {
            setActiveDialog(DIALOGS.SAVED_ADDRESSES);

            return;
        }

        setActiveDialog(DIALOGS.NEW_ADDRESS);
    }, [predefinedAddresses, setActiveDialog]);

    const onCloseModal = useCallback(() => setActiveDialog(null), [setActiveDialog]);

    const onAddAddress = useCallback(() => {
        setActiveDialog(DIALOGS.NEW_ADDRESS);
    }, [setActiveDialog]);

    const currentValue = useMemo(() => {
        if (activeDialog === DIALOGS.NEW_ADDRESS && predefinedAddresses.length > 0) {
            return undefined;
        }

        return value;
    }, [activeDialog, predefinedAddresses.length, value]);

    const currentDefaultLocation = useMemo(() => {
        if (activeDialog === DIALOGS.NEW_ADDRESS && predefinedAddresses.length > 0) {
            return '';
        }

        return defaultLocation;
    }, [activeDialog, defaultLocation, predefinedAddresses.length]);

    const pickupPointId = useMemo(() => currentValue?.pickupPointId || '', [currentValue?.pickupPointId]);

    return (
        <Fragment>
            <PickupNewAddressDialog
                key={pickupPointId}
                isOpen={activeDialog === DIALOGS.NEW_ADDRESS}
                order={order}
                reinitializeOrderForm={reinitializeOrderForm}
                storeSlug={storeSlug}
                value={currentValue}
                defaultLocation={currentDefaultLocation}
                onClose={onCloseModal}
                onChange={onChange}
                submitButton={submitButton}
            />

            <PickupSavedAddressesDialog
                isOpen={activeDialog === DIALOGS.SAVED_ADDRESSES}
                items={predefinedAddresses}
                onClose={onCloseModal}
                onAddAddress={onAddAddress}
                onChange={onChange}
            />

            {children({ onOpenModal })}
        </Fragment>
    );
};
